import { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthProvider";
import { invitePreview, verifySignupCode, resendSignupCode } from "../lib/auth";
import { C } from "../customers/theme";

// Dark-theme styles matching the existing app login.
const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: "'Inter', system-ui, sans-serif", padding: 16, boxSizing: "border-box" },
  card: { background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 400, maxWidth: "100%", boxSizing: "border-box", boxShadow: "0 20px 60px rgba(0,0,0,0.55)" },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 },
  input: { width: "100%", padding: "10px 12px", marginBottom: 14, background: "#0f141d", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: "border-box" },
  btn: { width: "100%", padding: 11, background: C.primary, color: "#06222a", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  link: { background: "none", border: "none", color: C.primary, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0 },
  err: { color: C.danger, fontSize: 12, marginBottom: 14, fontWeight: 600 },
  ok: { color: C.success, fontSize: 13, marginBottom: 14, fontWeight: 600, lineHeight: 1.5 },
  invite: { color: C.primary, fontSize: 13, marginBottom: 14, fontWeight: 600, lineHeight: 1.5 },
  notice: { color: "#94a3b8", fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 },
  // Password field: the input keeps its own look, the wrapper owns the spacing
  // so wrapping it changed no vertical rhythm.
  pwWrap: { position: "relative", marginBottom: 14 },
  eye: {
    position: "absolute", top: 0, right: 0, height: 39, width: 40,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "none", border: "none", padding: 0, cursor: "pointer",
    color: "#94a3b8", fontSize: 15, lineHeight: 1,
  },
  code: {
    width: "100%", padding: "12px 14px", marginBottom: 14, background: "#0f141d",
    border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
    fontSize: 22, fontWeight: 700, letterSpacing: 8, textAlign: "center",
    boxSizing: "border-box", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  addr: { color: C.text, fontSize: 13, fontWeight: 700, wordBreak: "break-all" },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Keeps the "Join <company>" heading/button from overflowing the 400px card —
// truncate a very long name rather than letting the button wrap.
const truncateName = (s, max = 26) => (s && s.length > max ? `${s.slice(0, max - 1)}…` : s);

export default function AuthScreen() {
  const { signIn, signUp, isConfigured } = useAuth();
  const [mode, setMode] = useState("signin"); // "signin" | "signup" | "verify"
  const [form, setForm] = useState({ email: "", password: "", companyName: "", fullName: "" });
  // CC41a. The toggle is per-render state, never persisted anywhere: a "remember
  // that I like my password visible" setting is a shoulder-surfing default.
  const [showPassword, setShowPassword] = useState(false);
  // The address the code actually went to. Held separately from form.email so
  // that "Wrong address? Start over" can repopulate the form without the two
  // ever disagreeing about which address is being confirmed.
  const [verifyEmail, setVerifyEmail] = useState("");
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  // Invite awareness — no router in this app; a link of the form
  // https://<host>/?invite=TOKEN is read straight off window.location.search.
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);     // { company_name, role, email } — a VALID pending, unexpired invite
  const [inviteNotice, setInviteNotice] = useState("");   // quiet fallback line for an unknown/revoked/expired token
  const passwordRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite");
    if (!token) return;
    (async () => {
      let preview = null;
      try {
        preview = await invitePreview(token);
      } catch {
        // invitePreview() returns null for an unknown token but THROWS on a
        // real failure (network error, a missing anon grant on the
        // function, etc). AuthScreen is the FRONT DOOR of the entire
        // product — if that throw escaped, a failing invite preview could
        // break sign-in for EVERYONE, including people who never had an
        // invite. Swallow it, treat it as "no invite", and let the normal
        // login screen render completely untouched.
        return;
      }
      // invite_preview deliberately returns a row (with status telling the
      // truth) for accepted and revoked invites too, and no row at all for
      // an unknown token — so validity is NOT "a row came back", it is
      // specifically status === 'pending' and not expired.
      const valid = !!preview && preview.status === "pending" && !preview.expired;
      setInviteToken(token);
      setMode("signup");
      if (valid) {
        setInviteInfo(preview);
        setForm((p) => ({ ...p, email: preview.email }));
      } else {
        setInviteNotice("That invite link is no longer valid. You can still create your own account below, or ask for a new link.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FOCUS FIX: autoFocus is mount-only in React — it fires when an element
  // first mounts and does nothing when a prop later flips to true. The invite
  // preview above is asynchronous, so by the time inviteInfo arrives the
  // Password input has already mounted; an autoFocus prop on it would be
  // inert. A ref + effect fires .focus() explicitly once the invite is known
  // to be valid, which is the only reliable way to do this here.
  useEffect(() => {
    if (mode === "signup" && inviteInfo && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [mode, inviteInfo]);

  // REQUIRED BEHAVIOUR, not a nicety: the toggle resets to hidden whenever the
  // mode changes. Switching sign-up -> sign-in with the password still revealed
  // would leave a filled, visible password box on screen after a deliberate
  // navigation, which is the one moment the user has stopped watching it.
  useEffect(() => { setShowPassword(false); }, [mode]);

  // Resend cooldown. Supabase rate-limits resends server-side regardless; this
  // exists so the button tells the truth about when it will work, instead of
  // letting someone hammer it into an opaque 429.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  // Strips everything that is not a digit, so a pasted "123 456" or "code: 123456"
  // still lands correctly rather than being silently truncated by maxLength.
  const setCodeDigits = (e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6));

  const startOver = () => {
    setMode("signup");
    setCode("");
    setError("");
    setNotice("");
    setResendIn(0);
    setForm((p) => ({ ...p, email: verifyEmail }));
  };

  const resend = async () => {
    setError("");
    setNotice("");
    try {
      await resendSignupCode({ email: verifyEmail });
      setResendIn(60);
      setNotice("A new code is on its way.");
    } catch (err) {
      // Supabase's own wording distinguishes "too many requests" from everything
      // else, and that distinction is the whole value — see src/lib/auth.js.
      setError(err.message || "Could not send a new code.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "verify") {
        if (code.length !== 6) throw new Error("Enter the 6-digit code from your email.");
        // On success Supabase establishes a session; AuthProvider's existing
        // onAuthStateChange listener loads the app. Nothing is done with the
        // returned session here on purpose — two places handling sign-in is how
        // they drift.
        await verifySignupCode({ email: verifyEmail, token: code });
      } else if (mode === "signup") {
        // Joining a company via an invite — not founding one — so the
        // company-name requirement does not apply.
        if (!inviteInfo && !form.companyName.trim()) throw new Error("Company name is required.");
        if (form.password.length < 6) throw new Error("Password must be at least 6 characters.");
        const data = await signUp(form);
        if (inviteToken) {
          // The invite is consumed at signUp() time (handle_new_user() fires
          // on INSERT into auth.users, not on email confirmation), so it is
          // safe — and correct — to clear it from the URL right away. Strips
          // ONLY the invite param, not the whole query string, in case others
          // ever coexist.
          const url = new URL(window.location.href);
          url.searchParams.delete("invite");
          window.history.replaceState(null, "", url.pathname + url.search + url.hash);
        }
        if (!data.session) {
          // CC41a. Email confirmation is enabled, so Supabase has emailed a
          // 6-digit code. The OLD copy here said "check your email to confirm
          // your address, then sign in" and sent the user to the sign-in screen
          // — which described a CLICK-A-LINK flow and, worse, dead-ended anyone
          // who came back to a form that could not accept a code. Go to the code
          // step instead, carrying the address so nobody retypes it.
          setVerifyEmail(form.email.trim().toLowerCase());
          setCode("");
          setResendIn(60);
          setMode("verify");
          setNotice("We've emailed you a 6-digit code. Enter it below to finish setting up your account.");
        }
        // If a session exists, AuthProvider's listener takes over and the app loads.
      } else {
        await signIn({ email: form.email, password: form.password });
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.page}>
      <form onSubmit={submit} style={styles.card}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src={`${process.env.PUBLIC_URL}/logo-msquared.png`} alt="Msquared" style={{ width: 140, height: "auto", borderRadius: 14, display: "block", margin: "0 auto" }} />
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>
            {mode === "verify"
              ? "Confirm your email address"
              : mode === "signup"
                ? (inviteInfo ? `Join ${truncateName(inviteInfo.company_name)}` : "Create your company workspace")
                : "Sign in to your workspace"}
          </div>
        </div>

        {!isConfigured && <div style={styles.err}>Backend not configured — set REACT_APP_SUPABASE_* in .env.</div>}

        {mode !== "verify" && (
          <>
        {mode === "signup" && inviteInfo && (
          <div style={styles.invite}>
            You've been invited to join <strong>{inviteInfo.company_name}</strong> as <strong>{cap(inviteInfo.role)}</strong>.
          </div>
        )}
        {mode === "signup" && !inviteInfo && inviteNotice && <div style={styles.notice}>{inviteNotice}</div>}

        {mode === "signup" && !inviteInfo && (
          <>
            <label style={styles.label}>Company name *</label>
            <input style={styles.input} value={form.companyName} onChange={set("companyName")} placeholder="Acme Ltd" autoFocus />
            <label style={styles.label}>Your name</label>
            <input style={styles.input} value={form.fullName} onChange={set("fullName")} placeholder="Jane Doe" />
          </>
        )}
        {mode === "signup" && inviteInfo && (
          <>
            <label style={styles.label}>Your name</label>
            <input style={styles.input} value={form.fullName} onChange={set("fullName")} placeholder="Jane Doe" />
          </>
        )}

        <label style={styles.label}>Email</label>
        <input
          style={{ ...styles.input, opacity: mode === "signup" && inviteInfo ? 0.7 : 1 }}
          type="email" value={form.email} onChange={set("email")} placeholder="you@example.com"
          autoFocus={mode === "signin"} readOnly={mode === "signup" && !!inviteInfo}
        />

        <label style={styles.label}>Password</label>
        {/* position:relative wrapper + absolutely positioned control, and 44px of
            right padding on the input so the value can never run under the eye. */}
        <div style={styles.pwWrap}>
          <input
            ref={passwordRef}
            style={{ ...styles.input, marginBottom: 0, paddingRight: 44 }}
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={set("password")}
            placeholder="••••••••"
            /* One input serves both modes, so the hint has to switch with mode —
               otherwise a password manager offers to fill a NEW password during
               sign-in, or offers the saved one during sign-up.
               ⛔ NEVER autoComplete="off" here. It fights password managers, and
               pushing people back towards typing a password they can remember is
               a security DOWNGRADE dressed up as one. */
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
          />
          <button
            /* ⛔ type="button" IS NOT OPTIONAL. The default for a <button> inside
               a <form> is type="submit" — a bare one here would submit the
               signup every time somebody peeked at what they had typed. */
            type="button"
            style={styles.eye}
            onClick={() => setShowPassword((v) => !v)}
            /* The label changes with state, so a screen reader announces what the
               control will DO next rather than a fixed "toggle password". */
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            title={showPassword ? "Hide password" : "Show password"}
          >
            <span aria-hidden="true">{showPassword ? "🙈" : "👁"}</span>
          </button>
        </div>

          </>
        )}

        {mode === "verify" && (
          <>
            {/* THE ADDRESS IS SHOWN BACK, AND THAT IS THE POINT OF THE SCREEN.
                A typo in the address is the single most common reason a code
                "never arrives"; if it is not on screen the user has no way to
                see it and will blame the email. */}
            <div style={styles.notice}>
              We sent a 6-digit code to<br />
              <span style={styles.addr}>{verifyEmail}</span>
            </div>

            <label style={styles.label} htmlFor="cc41-code">6-digit code</label>
            <input
              id="cc41-code"
              style={styles.code}
              value={code}
              onChange={setCodeDigits}
              /* inputMode numeric brings up the number pad on mobile without
                 type="number", which would add spinners and strip leading zeros.
                 one-time-code lets iOS and Android offer the code straight from
                 the notification. */
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              autoFocus
              aria-label="6-digit verification code"
            />
          </>
        )}

        {error && <div style={styles.err}>{error}</div>}
        {notice && <div style={styles.ok}>{notice}</div>}

        <button type="submit" style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }} disabled={busy || !isConfigured}>
          {busy ? "Please wait…" : mode === "verify"
            ? "Verify and continue"
            : mode === "signup"
              ? (inviteInfo ? `Join ${truncateName(inviteInfo.company_name)}` : "Create workspace")
              : "Sign in"}
        </button>

        <div style={{ marginTop: 18, textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
          {mode === "verify" ? (
            <>
              <button
                type="button"
                style={{ ...styles.link, opacity: resendIn > 0 ? 0.5 : 1, cursor: resendIn > 0 ? "default" : "pointer" }}
                onClick={resend}
                disabled={resendIn > 0 || busy}
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
              <div style={{ marginTop: 10 }}>
                <button type="button" style={styles.link} onClick={startOver}>Wrong address? Start over</button>
              </div>
            </>
          ) : mode === "signup" ? (
            <>Already have an account?{" "}
              <button type="button" style={styles.link} onClick={() => { setMode("signin"); setError(""); setNotice(""); }}>Sign in</button>
            </>
          ) : (
            <>New here?{" "}
              <button type="button" style={styles.link} onClick={() => { setMode("signup"); setError(""); setNotice(""); }}>Create a company workspace</button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
